import { LegislationApi, ProductApi, ResearchReviewApi } from '@core/api'
import SubstanceApi from '@core/api/request/substance'

const createReviewWorkspaceService = (
    api = ResearchReviewApi,
    substanceApi = SubstanceApi,
    productApi = ProductApi,
    legislationApi = LegislationApi,
) => ({
    getProcess(processId) {
        return api.getProcess(processId).then(response => response.data)
    },

    updateItem(processId, itemId, payload) {
        return api.updateItem(processId, itemId, payload)
    },

    completeProcess(processId, payload) {
        return api.completeProcess(processId, payload)
    },

    getLegislations() {
        return legislationApi.all().then(response => (
            response && Array.isArray(response.data) ? response.data : []
        ))
    },

    getSubstanceSummaries(ids) {
        return substanceApi.summary(ids).then(response => {
            const data = response && response.data ? response.data : {}

            return {
                items: Array.isArray(data.items) ? data.items : [],
                missingIds: Array.isArray(data.missingIds) ? data.missingIds : [],
            }
        })
    },

    getProductSummaries(ids = []) {
        const normalizedIds = Array.from(new Set(ids
            .map(id => Number(id))
            .filter(id => Number.isInteger(id) && id > 0)))
        const batches = []

        for (let index = 0; index < normalizedIds.length; index += 100) {
            batches.push(normalizedIds.slice(index, index + 100))
        }

        if (!batches.length) {
            return Promise.resolve({ items: [], missingIds: [] })
        }

        return Promise.all(batches.map(batch => productApi.getProductSummaries(batch)))
            .then(responses => responses.reduce((result, response) => {
                const data = response && response.data ? response.data : {}

                return {
                    items: result.items.concat(Array.isArray(data.items) ? data.items : []),
                    missingIds: result.missingIds.concat(
                        Array.isArray(data.missingIds) ? data.missingIds : [],
                    ),
                }
            }, { items: [], missingIds: [] }))
    },

    resolveSubstanceRegulations(ids = []) {
        const normalizedIds = Array.from(new Set(ids
            .map(id => Number(id))
            .filter(id => Number.isInteger(id) && id > 0)))
        const batches = []

        for (let index = 0; index < normalizedIds.length; index += 200) {
            batches.push(normalizedIds.slice(index, index + 200))
        }

        if (!batches.length) {
            return Promise.resolve({ items: [], missingSubstanceIds: [] })
        }

        return Promise.all(batches.map(batch => substanceApi.resolveRegulations(batch)))
            .then(responses => responses.reduce((result, response) => {
                const data = response && response.data ? response.data : {}

                return {
                    items: result.items.concat(Array.isArray(data.items) ? data.items : []),
                    missingSubstanceIds: result.missingSubstanceIds.concat(
                        Array.isArray(data.missingSubstanceIds) ? data.missingSubstanceIds : [],
                    ),
                }
            }, { items: [], missingSubstanceIds: [] }))
    },

    loadPdfPreview(fileReferenceId) {
        return api.resolveFileDownloadUrl(fileReferenceId)
            .then(response => (
                response && response.data ? response.data.downloadUrl : null
            ))
            .then(downloadUrl => {
                if (!downloadUrl) {
                    return { downloadUrl: '', previewUrl: '' }
                }

                return api.downloadFileByUrl(downloadUrl)
                    .then(response => {
                        const contentType = response && response.headers
                            ? response.headers['content-type'] || ''
                            : ''

                        if (!contentType.includes('application/pdf')) {
                            return { downloadUrl, previewUrl: '' }
                        }

                        const blob = new Blob([response.data], { type: 'application/pdf' })

                        return {
                            downloadUrl,
                            previewUrl: URL.createObjectURL(blob),
                        }
                    })
                    .catch(() => ({ downloadUrl, previewUrl: '' }))
            })
    },
})

export { createReviewWorkspaceService }
export default createReviewWorkspaceService
